import React, { Children } from "react";

import { defaultFallbackInView } from "react-intersection-observer";

import BusbudTerms from "@busbud/busbud-terms";

import { AppContextProvider } from "@app/context/app-context";
import { WhitelabelClientContextProvider } from "@app/context/whitelabel-context";
import { axiosBrowser } from "@app/lib/http-clients/axios-browser";
import type AdyenCheckoutSDKService from "@app/services/adyen-checkout-sdk";
import { getApiBrowser } from "@app/services/api-factory";
import { LiteServiceProviderClient } from "@app/services/context-providers/lite-service-provider-client-base";
import { ServiceProviderClientErrorBoundary } from "@app/services/context-providers/service-provider-error-boundary";
import { GeoDataService } from "@app/services/geo-data-service";
import type { SentryClient } from "@app/services/sentry-facade";
import type Tracker from "@app/tracking/tracker";
import type { BBType } from "@app/types/bb-types";
import type { GeoDataCountry } from "@app/types/geo-data";

import type { TranslatorI18N } from "../translator";
import type { ValidTranslationKey } from "../translator.keys";
import type YunoCheckoutSDKService from "../yuno-checkout-sdk";

import IntlProvider from "../intl-provider";
import Translator from "../translator";
import UrlBuilder from "../url-builders/url-builder";

/**
 * The useInView hook uses IntersectionObserver API under the hook.
 * Some old browsers do not have this API implemented which is causing an error to be thrown and the react app to crash.
 * This function call set the default value returned by the hook if the IntersectionObserver API is not implemented.
 * Setting it to true means that every elements are displayed in the view port so everything should be mounted in the DOM.
 */
defaultFallbackInView(true);

export interface ServiceProviderClientProps {
  BB: BBType;
  I18N: TranslatorI18N<ValidTranslationKey>;
  GEO_DATA?: GeoDataCountry[];
  REDIRECT?: string;
  sentry?: SentryClient;
  AdyenCheckoutSdkServiceClass?: typeof AdyenCheckoutSDKService;
  YunoCheckoutSDKServiceClass?: typeof YunoCheckoutSDKService;
  children: React.ReactNode;
}

const ServiceProviderClient: React.FC<ServiceProviderClientProps> = ({
  BB,
  I18N,
  GEO_DATA,
  REDIRECT,
  sentry,
  AdyenCheckoutSdkServiceClass,
  YunoCheckoutSDKServiceClass,
  children
}) => {
  const tracker: Tracker = (BB as any).tracker || window.tracker;
  const { locale, language, locale_links, browser_locale, csrf } = BB.config;
  const currency = BB.config.currency.code;
  const device = BB.device;
  const features = BB.features;
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const translator = new Translator(I18N[locale].phrases);
  const api = getApiBrowser(BB.NAPI_URL, BB);
  const httpClient = axiosBrowser;
  const whitelabel_name =
    BB.whitelabel ?? BB.whitelabel_context?.whitelabel?.name;
  const urlBuilder = new UrlBuilder({
    locale: locale,
    whitelabel: whitelabel_name,
    original_url: window.location.pathname + window.location.search,
    redirect_url: REDIRECT
  });

  const intlProvider = new IntlProvider({
    locale,
    currency,
    sentry
  });
  // @ts-expect-error TS2532 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
  const termsBuilder = new BusbudTerms(I18N[locale].terms_phrases, locale);
  const geoData = new GeoDataService(locale, GEO_DATA || []);
  const user_country_code = BB.user_country_code;
  const user_region = BB.user_region;

  const adyenCheckoutSdkService = AdyenCheckoutSdkServiceClass
    ? new AdyenCheckoutSdkServiceClass(
        locale,
        BB.config.ADYEN_CHECKOUT_SDK_ENVIRONMENT,
        translator,
        device
      )
    : undefined;

  const yunoCheckoutSdkService = YunoCheckoutSDKServiceClass
    ? new YunoCheckoutSDKServiceClass(locale, translator, device)
    : undefined;

  const context = {
    api,
    browser_locale,
    csrf,
    device,
    features,
    geoData,
    httpClient,
    intlProvider,
    language,
    locale,
    locale_links,
    sentry: sentry || null,
    termsBuilder,
    tracker,
    translator,
    liteTranslator: translator,
    urlBuilder,
    user_country_code,
    user_region,
    whitelabel: whitelabel_name,
    entity: BB.entity || null,
    adyenCheckoutSdkService,
    yunoCheckoutSdkService
  };

  return (
    <ServiceProviderClientErrorBoundary sentry={sentry}>
      <WhitelabelClientContextProvider BB={BB}>
        <LiteServiceProviderClient BB={BB} I18N={I18N}>
          <AppContextProvider value={context}>
            {Children.only(children)}
          </AppContextProvider>
        </LiteServiceProviderClient>
      </WhitelabelClientContextProvider>
    </ServiceProviderClientErrorBoundary>
  );
};

export default ServiceProviderClient;
